/**
 * Workspace State
 * Global state for current organization and project selection
 * Syncs with URL structure: /p/{projectId}/... and /o/{orgId}/...
 */

import { localStorageProp, Signal } from '@tempots/dom'
import {
  organizations as orgsService,
  projects as projectsService,
} from '@/services/gateway'
import { AsyncResult } from '@tempots/std'
import { Organization, Project } from '@/services'
import { formatCredits } from '@/utils'

function getKey(suffix: string) {
  return `vs:${suffix}`
}

// ============================================================================
// BigInt-safe serialization for localStorage
// ============================================================================

/**
 * Serialize value to JSON string, converting BigInt to tagged string format
 */
function serializeWithBigInt<T>(value: T): string {
  return JSON.stringify(value, (_key, val) =>
    typeof val === 'bigint' ? `__bigint__${val.toString()}` : val
  )
}

/**
 * Deserialize JSON string, converting tagged strings back to BigInt
 */
function deserializeWithBigInt<T>(str: string): T {
  return JSON.parse(str, (_key, val) => {
    if (typeof val === 'string' && val.startsWith('__bigint__')) {
      return BigInt(val.slice(10))
    }
    return val
  })
}

// ============================================================================
// State
// ============================================================================

/** Currently selected organization */
const currentOrg = localStorageProp<Organization>({
  defaultValue: {} as Organization,
  key: getKey('org'),
  serialize: serializeWithBigInt,
  deserialize: deserializeWithBigInt,
})

/** Currently selected project */
const currentProject = localStorageProp<Project>({
  defaultValue: {} as Project,
  key: getKey('proj'),
})

const projects = localStorageProp<Record<string, Project[]>>({
  defaultValue: {},
  key: getKey('projs'),
})

/** List of all organizations with their projects */
const organizations = localStorageProp<AsyncResult<Organization[], string>>({
  defaultValue: AsyncResult.notAsked,
  key: getKey('orgs'),
  serialize: serializeWithBigInt,
  deserialize: deserializeWithBigInt,
})

/** Credit balance for current organization */
const currentOrgCredits = currentOrg.map((v) => v?.creditBalance ?? 0n)

function changeProject(id: string) {
  const proj = findProject(id)
  if (proj == null) {
    console.error('Unable to switch project, cannot find project id:', id)
    return
  }
  const org = findOrganization(proj.orgId)
  if (org == null) {
    console.error(
      'Unable to switch project, cannot find organization for project id',
      id
    )
  }
  currentProject.set(proj)
  currentOrg.set(org)
}

function changeOrganization(id: string) {
  const org = findOrganization(id)
  if (org == null) {
    console.error(
      'Unable to switch organization, cannot find organization id',
      id
    )
  }
  currentOrg.set(org)
  const proj = currentProject.value
  if (proj == null || proj.orgId !== id) {
    const projectList = projects.value[id]
    currentProject.set(findDefaultProject(projectList) ?? projectList[0])
  }
}

// ============================================================================
// Credit Management
// ============================================================================

function getCurrentOrgId() {
  return currentOrg.value?.id
}

function getCurrentProjectId() {
  return currentProject.value?.id
}

/**
 * Refresh credits for current organization
 * Call this after operations that consume credits
 */
async function refreshCreditsForOrg(orgId: string): Promise<void> {
  try {
    const org = await orgsService.getOrganization(orgId)
    updateOrganizationCredits(orgId, org.creditBalance)
  } catch (error) {
    console.error('Failed to refresh credits:', error)
  }
}

async function refreshCredits(): Promise<void> {
  const orgId = getCurrentOrgId()
  if (orgId == null) return
  await refreshCreditsForOrg(orgId)
}

// ============================================================================
// URL Utilities
// ============================================================================

/**
 * Get the project URL path prefix for the current project
 * @param projectId - Optional project ID (uses current if not provided)
 * @returns The path prefix like "/p/prj_123"
 */
function getProjectPath(projectId?: string): string | null {
  const id = projectId ?? getCurrentProjectId()
  if (!id) return null
  return `/p/${id}`
}

/**
 * Get the organization URL path prefix for the given org
 * @param orgId - Optional organization ID (uses current if not provided)
 * @returns The path prefix like "/o/org_123"
 */
function getOrgPath(orgId?: string): string | null {
  const id = orgId || getCurrentOrgId()
  if (!id) return null
  return `/o/${id}`
}

/**
 * Build a project-scoped URL
 * @param path - The path within the project (e.g., "/files", "/api-keys")
 * @param projectId - Optional project ID (uses current if not provided)
 */
function projectUrl(path: string, projectId?: string | null): string | null {
  const prefix = getProjectPath(projectId ?? undefined)
  if (prefix == null) return null
  return `${prefix}${path.startsWith('/') ? path : `/${path}`}`
}

function findPersonalOrg(organizations: Organization[]) {
  return organizations.find((org) => org.isPersonal)
}

function findDefaultProject(projects: Project[]) {
  return projects.find((proj) => proj.isDefault)
}

let initialized = false
/**
 * Load workspace data from the API
 */
async function loadWorkspace(force = false): Promise<void> {
  if (initialized && !force) return
  initialized = true

  // load all organizations
  AsyncResult.effect(organizations.value, {
    success: (previous) => organizations.set(AsyncResult.loading(previous)),
    else: () => organizations.set(AsyncResult.loading()),
  })
  try {
    const { organizations: orgList } = await orgsService.listOrganizations()
    organizations.set(AsyncResult.success(orgList))
    if (orgList.length === 0) {
      organizations.set(
        AsyncResult.failure('Account is not associated to any organization')
      )
      return
    }

    // if current organization is notAsked, set personal organization as default
    // if not notAsked, check that it exists in the list, if it does, do nothing (maybe update content?)
    // if it does not exist, pick the personal one
    currentOrg.update((current) => {
      if (current == null) {
        return findPersonalOrg(orgList) || orgList[0]!
      } else {
        const corresponding = orgList.find((item) => item.id === current.id)
        if (corresponding == null) {
          return findPersonalOrg(orgList) || orgList[0]!
        } else {
          return corresponding
        }
      }
    })
  } catch (e) {
    organizations.set(AsyncResult.failure((e as Error)?.message ?? String(e)))
    return
  }

  const orgId = getCurrentOrgId()!
  try {
    const { projects: projectList } = await projectsService.listProjects(orgId)
    if (projectList.length === 0) {
      console.error('Failed to load projects')
      return
    }
    projects.update((prjs) => {
      return {
        ...prjs,
        [orgId]: projectList,
      }
    })
    currentProject.update((current) => {
      if (current == null) {
        return findDefaultProject(projectList) ?? projectList[0]
      } else {
        const corresponding = projectList.find(
          (item) => item.id === getCurrentProjectId()
        )
        if (corresponding == null) {
          return findDefaultProject(projectList) ?? projectList[0]
        } else {
          return corresponding
        }
      }
    })
  } catch (e) {
    console.error('Failed to load projects', e)
  }
}

function updateData<T extends { id: string }>(id: string, payload: Partial<T>) {
  return (source: T): T => {
    if (source.id != id) return source
    const newOb = { ...source }
    for (const key in payload) {
      const value = payload[key]
      if (key === 'id' || value === undefined) continue
      newOb[key] = value
    }
    return newOb
  }
}

function updateOrganization(orgId: string, data: Partial<Organization>): void {
  const update = updateData<Organization>(orgId, data)
  organizations.update((result) =>
    AsyncResult.map(result, (orgs) => orgs.map(update))
  )
  currentOrg.update((v) => update(v))
}

/**
 * Update organization name in workspace state
 * Call this after updating an organization name via API
 */
function updateOrganizationName(orgId: string, newName: string): void {
  return updateOrganization(orgId, { name: newName })
}

function updateOrganizationCredits(orgId: string, newCredits: bigint): void {
  return updateOrganization(orgId, { creditBalance: newCredits })
}

function findProject(projId: string): Project {
  const all = projects.value
  if (all == null) throw new Error('Projects not loaded')
  for (const orgId in all) {
    for (const proj of all[orgId]) {
      if (proj.id == projId) return proj
    }
  }
  throw new Error(`Project ${projId} not found`)
}

function findOrganization(orgId: string): Organization {
  const orgList = AsyncResult.getOrElse(organizations.value, [])
  const org = orgList.find((org) => org.id === orgId)
  if (org == null) throw new Error(`Organization ${orgId} not found`)
  return org
}

/**
 * Update project name in workspace state
 * Call this after updating a project name via API
 */
function updateProject(projectId: string, data: Partial<Project>): void {
  const update = updateData<Project>(projectId, data)
  currentProject.update((v) => update(v))
  const project = findProject(projectId)
  if (project == null) return
  const orgId = project.orgId
  projects.update((projs) => ({
    ...projs,
    [orgId]: projs[orgId]?.map(update) ?? [],
  }))
}

function updateProjectName(projectId: string, newName: string): void {
  updateProject(projectId, { name: newName })
}

function addProject(orgId: string, project: Project): void {
  projects.update((projs) => ({
    ...projs,
    [orgId]: [...(projs[orgId] ?? []), project],
  }))
}

function addOrganization(organization: Organization): void {
  organizations.update((orgs) =>
    AsyncResult.map(orgs, (orgs) => [...orgs, organization])
  )
}

export const Workspace = {
  projects: projects as Signal<Record<string, Project[]>>,
  organizations: organizations as Signal<AsyncResult<Organization[], string>>,
  project: currentProject as Signal<Project>,
  organization: currentOrg as Signal<Organization>,
  credits: currentOrgCredits,
  formattedCredits: currentOrgCredits.map((v) =>
    formatCredits(v, { includeSign: false, includeSuffix: false })
  ),
  setOrganizationName: updateOrganizationName,
  setProjectName: updateProjectName,
  setOrganizationCredits: (credits: bigint) => {
    const id = getCurrentOrgId()
    if (id == null) return
    updateOrganizationCredits(id, credits)
    refreshCreditsForOrg(id)
  },
  getProjectPath,
  getOrgPath,
  projectUrl,
  loadWorkspace,
  projectId: currentProject.map((p) => p?.id ?? null),
  organizationId: currentOrg.map((o) => o?.id ?? null),
  changeOrganization,
  changeProject,
  refreshCredits,
  refreshCreditsForOrg,
  addProject,
  addOrganization,
  updateProjectName,
  getProjectById: findProject,
}
